import React, { useMemo } from 'react';
import { BsDashCircle } from 'react-icons/bs';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Text, Tooltip
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import PropTypes from 'prop-types';

import { hoverStyle } from '../MultiSelectDropdown/styles';
import SearchInputGroup from '../SearchInputGroup';
import LoadingSpinner from '../Spinner';

const MultiSelectInfiniteList = ({
  isLoading = true,
  data = [],
  idKey = 'id',
  labelKey,
  values = [],
  onSelect = () => {},
  onSearchTermChange = () => {},
  fetchNextData,
  hasMore = false,
  scrollHeight = '295px',
  popoverButtonShouldPulse = false,
  popoverButtonOnClick = () => {},
  emptyResultComponent = () => (<Box justifyContent="center" p={4}><Text>None</Text></Box>),
  toolTipLabel = () => {},
  renderArchivedKey = null
}) => {
  const initialItems = useMemo(() => data.map(item => ({
    value: item[idKey],
    label: typeof labelKey === 'function' ? labelKey(item) : item[labelKey],
    toolTip: toolTipLabel ? toolTipLabel(item) : null,
    isArchived: renderArchivedKey ? item[renderArchivedKey] : false
  })), [data, idKey, labelKey]);

  const normalizedValues = useMemo(() => values.map(val => ({
    value: val[idKey],
    label: typeof labelKey === 'function' ? labelKey(val) : val[labelKey],
    toolTip: toolTipLabel ? toolTipLabel(val) : null,
    isArchived: renderArchivedKey ? val[renderArchivedKey] : false
  })), [idKey, labelKey, values]);

  const onSearchChange = ({ target: { value } }) => {
    onSearchTermChange(value);
  };

  const onSelectedItemChange = (item, isChecked) => {
    const selectedItem = data.find(dataItem => dataItem[idKey] === item.value);
    onSelect(selectedItem ?? { [idKey]: item.value, [labelKey]: item.label }, isChecked);
  };

  const pulse = keyframes`
    0% {transform: scale(1,1);}
    50% {opacity: 0.5;}
    100% {transform: scale(1.5); opacity: 0.3;}
  `;

  return (
    <Box>
      <Flex flexFlow="row nowrap" justifyContent="space-between" py={4}>
        <SearchInputGroup width={normalizedValues.length >= 1 ? '340px' : '350px'} onChange={onSearchChange} />
        { normalizedValues.length >= 1 && (
          <Popover>
            <PopoverTrigger>
              <Button
                animation={popoverButtonShouldPulse ? `${pulse} 1s ease infinite` : undefined}
                isDisabled={isLoading}
                mx={2}
                variant="primary"
                onClick={popoverButtonOnClick}
              >
                {`+${normalizedValues.length}`}
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverBody>
                <Box maxH="400px" overflow="auto" p={4} width="400px">
                  <Text color="greyScale.700" mb={4} textStyle="bodyMd500">
                    Selected:
                  </Text>
                  {normalizedValues.map(item => (
                    <Tooltip
                      shouldWrapChildren
                      isDisabled={!item.toolTip}
                      key={item.value}
                      label={item.toolTip}
                      placement="right-end"
                    >
                      <HStack minH="40px" my={2}>
                        <IconButton
                          icon={<Icon as={BsDashCircle} />}
                          variant="textError"
                          onClick={() => onSelectedItemChange(item, false)}
                        />
                        <Text>{item.label}</Text>
                      </HStack>
                    </Tooltip>
                  ))}
                </Box>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </Flex>

      { isLoading
        ? (
          <Box>
            <LoadingSpinner minHeight="200px" />
          </Box>
        )
        : (
          <InfiniteScroll
            dataLength={data.length}
            hasMore={hasMore}
            height={initialItems.length < 6 ? 'auto' : (scrollHeight)}
            loader={(
              <Text color="greyScale.500" pt={2} textAlign="center" textStyle="bodySm">
                Loading...
              </Text>
            )}
            next={fetchNextData}
          >
            { (!initialItems.length && !isLoading)
              ? (
                <>
                  {emptyResultComponent()}
                </>
              )
              : initialItems.map(item => (
                <Box
                  _hover={hoverStyle}
                  alignItems="center"
                  bgColor={item.isArchived ? 'greyScale.700' : null}
                  borderRadius="md"
                  color={item.isArchived ? 'white' : 'greyScale.900'}
                  key={item.value}
                  minH="40px"
                  p={2}
                >
                  <Tooltip
                    shouldWrapChildren
                    isDisabled={!item.toolTip}
                    key={item.value}
                    label={item.toolTip}
                    placement="right-end"
                  >
                    <Checkbox
                      isChecked={normalizedValues.findIndex(value => value.value === item.value) >= 0}
                      onChange={e => onSelectedItemChange(item, e.target.checked)}
                    >
                      {item.label}
                    </Checkbox>
                  </Tooltip>
                </Box>
              ))}
          </InfiniteScroll>
        )}
    </Box>
  );
};

MultiSelectInfiniteList.propTypes = {
  isLoading: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.object),
  idKey: PropTypes.string,
  labelKey: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.object),
  onSelect: PropTypes.func,
  onSearchTermChange: PropTypes.func,
  fetchNextData: PropTypes.func,
  hasMore: PropTypes.bool,
  scrollHeight: PropTypes.string,
  popoverButtonShouldPulse: PropTypes.bool,
  popoverButtonOnClick: PropTypes.func,
  emptyResultComponent: PropTypes.func,
  toolTipLabel: PropTypes.func,
  renderArchivedKey: PropTypes.string
};

export default MultiSelectInfiniteList;
